// The three `$setValidity` checks behind the criteria builder's error labels
// (`search.page.component.ng1.ts:1085, 1248, 1357`), as pure functions.
//
// ⚠️ THE PORT RENDERED NONE OF THESE MESSAGES, and that is not cosmetic: production refuses to
// SAVE while any of them stands, so a user whose form is invalid sees a Save button that does
// nothing and no reason why. Measured against build 354 on the same tenant — adding an empty
// second row put "Please fill in skills from the list" on production and nothing at all here.
//
// ⚠️ "EMPTY ON LOAD" IS WHY A BLANK FORM IS NOT AN ERROR. Each validator short-circuits when
// the section holds exactly ONE row and that row is still the untouched empty one, so opening
// a new alert does not greet the user with three complaints. Drop that clause and the blank
// form turns red.
import type { FieldOfExpertiseRow } from './FieldOfExpertiseSection.tsx'
import type { SkillCriteriaRow } from './SkillSetSection.tsx'
import type { KeywordCondition } from './KeywordSearchSection.tsx'

function sameShape(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * `validateSkillCriteria` — every row must carry at least one skill.
 *
 * ⚠️ "EMPTY ON LOAD" IS A PREDICATE, NOT A SHAPE COMPARISON — the same correction the
 * field-of-expertise check needed, and for a sharper reason here. The screen's INITIAL state is
 * a bare `{}`, replaced by `emptySkillRow()` once the alert seeds; both have no skills, so the
 * memo's signature does not change and the verdict computed against `{}` is the one that
 * sticks. That put "Please fill in skills from the list" on an untouched form, which is what
 * the pixel gate photographed at 1146px.
 */
export function skillsCriteriaValid(rows: readonly SkillCriteriaRow[]): boolean {
  const first = rows[0]
  if (rows.length === 1 && first !== undefined && (first.skills?.length ?? 0) === 0) return true
  return rows.every((row) => (row.skills?.length ?? 0) > 0)
}

/** `validateKeywordCriteria` — every condition must carry at least one term. See the note on
 *  `skillsCriteriaValid`: a lone condition with no terms is the starting state, whatever shape
 *  it happens to hold. Changing its FIELD select must not turn an untouched form red, and
 *  upstream never revalidates on that select at all. */
export function keywordsCriteriaValid(conditions: readonly KeywordCondition[]): boolean {
  const first = conditions[0]
  if (conditions.length === 1 && first !== undefined && (first.terms?.length ?? 0) === 0) return true
  return conditions.every((condition) => (condition.terms?.length ?? 0) > 0)
}

/** `isOtherPositionsFieldOfExpertiseCriteriaValid` — an "Other fields" row needs titles. */
export function otherPositionsValid(row: FieldOfExpertiseRow, otherFieldsFieldName?: string): boolean {
  if (otherFieldsFieldName === undefined) return true
  if (row.field !== otherFieldsFieldName) return true
  return (row.otherPositions?.length ?? 0) > 0
}

export interface FieldOfExpertiseValidityInput {
  rows: readonly FieldOfExpertiseRow[]
  otherFieldsFieldName?: string
  extendedTitleClass?: boolean
  emptyRow?: FieldOfExpertiseRow
}

/**
 * "The row the form seeds, still untouched" — upstream's `angular.equals(row, getNewFOE())`.
 *
 * ⚠️ COMPARING TWO INDEPENDENTLY BUILT EMPTY ROWS IS NOT THE SAME TEST, and it failed on the
 * one screen that matters. Upstream can compare against `getNewFOE()` because its flags are on
 * `window.careero` synchronously, so the row the form seeded and the row the check builds are
 * the same object shape by construction. Here the flags arrive with the view model, the seed
 * and the check can be built from different values of them, and a mismatch makes an untouched
 * form report itself invalid — which put "Please select a field" on a pristine screen where
 * production shows nothing. Measured in the pixel gate at 1146px.
 *
 * So the clause is expressed as what it MEANS: nothing chosen anywhere in the row. The
 * sentinel counts as "nothing", because with `extendedTitleClass` on it is what the seed puts
 * there.
 */
export function isPristineRow(row: FieldOfExpertiseRow, otherFieldsFieldName?: string): boolean {
  const fieldIsUnset = row.field === undefined || row.field === '' || row.field === otherFieldsFieldName
  return (
    fieldIsUnset &&
    (row.position ?? '') === '' &&
    (row.otherPositions?.length ?? 0) === 0 &&
    (row.minYears ?? null) === null &&
    (row.maxYears ?? null) === null
  )
}

/** `validateFieldOfExpertiseCriteria`, transcribed including its two-part conjunction. */
export function fieldOfExpertiseCriteriaValid({
  rows,
  otherFieldsFieldName,
  extendedTitleClass,
  emptyRow,
}: FieldOfExpertiseValidityInput): boolean {
  const first = rows[0]
  const isEmptyOnLoad =
    rows.length === 1 &&
    first !== undefined &&
    (isPristineRow(first, otherFieldsFieldName) || (emptyRow !== undefined && sameShape(first, emptyRow)))
  const areAllFieldsFilled = rows.every((row) => row.field !== undefined && row.field !== '')
  const areAllOtherFieldsFilled = rows
    .filter((row) => row.field === otherFieldsFieldName)
    .every((row) => otherPositionsValid(row, otherFieldsFieldName))
  // ⚠️ THE SINGLE-ROW ESCAPE HATCH IS FLAG-GATED. With `extendedTitleClass` on, one row is
  // allowed to carry no titles yet — which is what lets a brand-new form sit there quietly.
  const otherFieldsExempt = extendedTitleClass === true && rows.length === 1
  return (isEmptyOnLoad || areAllFieldsFilled) && (areAllOtherFieldsFilled || otherFieldsExempt)
}

/**
 * `validateFieldOfExpertiseOperator` — more than one row needs "Match all" / "Match one".
 *
 * The same rule `mapFieldOfExpertise` already enforces by withholding the whole section, which
 * is what made the missing message expensive: the criteria silently vanished and the screen
 * said nothing.
 */
export function fieldOfExpertiseOperatorValid(rowCount: number, operator: number | null): boolean {
  return rowCount <= 1 || operator !== null
}
